import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Store as StoreIcon,
  SupportAgent as SupportAgentIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  MonitorHeart as MonitorHeartIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { adminLogout } from '../../store/slices/adminSlice';

const drawerWidth = 260;

const adminMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard' },
  { text: 'User Management', icon: <PeopleIcon />, path: '/admin/users' },
  { text: 'Merchants', icon: <StoreIcon />, path: '/admin/merchants' },
  { text: 'Support Tickets', icon: <SupportAgentIcon />, path: '/admin/support' },
  { text: 'Earnings', icon: <AccountBalanceWalletIcon />, path: '/admin/earnings' },
  { text: 'System Health', icon: <MonitorHeartIcon />, path: '/admin/system' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/admin/settings' },
];

const AdminLayout: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { admin } = useSelector((state: RootState) => state.admin);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    dispatch(adminLogout());
    navigate('/admin/login', { replace: true });
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo Section */}
      <Box sx={{
        p: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        background: 'linear-gradient(135deg, #09486D 0%, #11C1EC 100%)',
      }}>
        <Box
          component="img"
          src="https://mal-pay.com/images/logo22.png"
          alt="MalPay"
          sx={{ height: 40, width: 'auto', filter: 'brightness(0) invert(1)' }}
          onError={(e: any) => { e.target.style.display = 'none'; }}
        />
        <Box>
          <Typography variant="h5" component="div" sx={{
            fontWeight: 800,
            color: 'white',
          }}>
            MalPay
          </Typography>
          <Chip
            label="ADMIN"
            size="small"
            sx={{
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontWeight: 700,
              fontSize: '0.65rem',
              height: 20,
              marginTop: 0.5,
            }}
          />
        </Box>
      </Box>

      {/* Navigation */}
      <List sx={{ px: 1, flex: 1, pt: 2 }}>
        {adminMenuItems.map((item) => {
          const isSelected = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={isSelected}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) {
                    setMobileOpen(false);
                  }
                }}
                sx={{
                  borderRadius: 3,
                  py: 1.2,
                  mx: 0.5,
                  color: isSelected ? '#11C1EC' : '#565972',
                  backgroundColor: isSelected ? 'rgba(9, 72, 109, 0.1)' : 'transparent',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(9, 72, 109, 0.1)',
                    color: '#11C1EC',
                    '& .MuiListItemIcon-root': {
                      color: '#11C1EC',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(9, 72, 109, 0.15)',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(9, 72, 109, 0.05)',
                  },
                }}
              >
                <ListItemIcon sx={{
                  minWidth: 40,
                  color: isSelected ? '#11C1EC' : '#7F839C',
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isSelected ? 700 : 500,
                    fontSize: '0.9rem',
                    color: isSelected ? '#11C1EC' : '#565972',
                  }}
                />
                {isSelected && (
                  <Box sx={{
                    width: 4,
                    height: 24,
                    borderRadius: 2,
                    background: 'linear-gradient(180deg, #11C1EC, #09486D)',
                    ml: 1,
                  }} />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ mx: 1 }} />

      {/* Admin Info */}
      <Box sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        backgroundColor: 'rgba(9, 72, 109, 0.05)',
        borderRadius: 2,
        m: 1,
      }}>
        <Avatar sx={{
          width: 36,
          height: 36,
          background: 'linear-gradient(135deg, #09486D, #11C1EC)',
          fontSize: '0.9rem',
          fontWeight: 700,
        }}>
          {admin?.name?.[0]?.toUpperCase() || 'A'}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#272A3A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {admin?.name || 'Admin'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#7F839C' }}>
            {admin?.role === 'super_admin' ? 'Super Admin' : 'Admin'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{
      display: 'flex',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #EDF2FA 0%, #F4F7FC 25%, #F8F8FC 50%, #F0F6FB 75%, #E8F0F9 100%)',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'fixed',
        top: 0,
        right: 0,
        width: '50vw',
        height: '50vh',
        background: 'radial-gradient(ellipse at 80% 20%, rgba(9, 72, 109, 0.06) 0%, transparent 70%)',
        pointerEvents: 'none',
        zIndex: 0,
      },
      '&::after': {
        content: '""',
        position: 'fixed',
        bottom: 0,
        left: '20%',
        width: '60vw',
        height: '40vh',
        background: 'radial-gradient(ellipse at 30% 80%, rgba(9, 72, 109, 0.04) 0%, transparent 70%)',
        pointerEvents: 'none',
        zIndex: 0,
      },
    }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: '100%',
          transition: theme.transitions.create(['width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          bgcolor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(9, 72, 109, 0.06)',
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, color: '#272A3A', display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}>
            <Typography variant="h6" noWrap component="div" sx={{
              fontWeight: 700,
              color: '#09486D',
            }}>
              {adminMenuItems.find(item => item.path === location.pathname)?.text || 'Admin Dashboard'}
            </Typography>
            <Chip
              label="ADMIN"
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #09486D 0%, #11C1EC 100%)',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.65rem',
              }}
            />
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <IconButton
            onClick={handleProfileMenuOpen}
            sx={{ '&:hover': { backgroundColor: 'rgba(9, 72, 109, 0.08)' } }}
          >
            <Avatar sx={{
              width: 36,
              height: 36,
              background: 'linear-gradient(135deg, #09486D, #11C1EC)',
              fontSize: '0.9rem',
              fontWeight: 700,
            }}>
              {admin?.name?.[0]?.toUpperCase() || 'A'}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Sidebar — Mobile (temporary) */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              boxShadow: '1px 0 20px rgba(9, 72, 109, 0.08)',
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Sidebar — Desktop (persistent) */}
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              background: 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(20px)',
              boxShadow: '1px 0 24px rgba(9, 72, 109, 0.08)',
              borderRight: '1px solid rgba(9, 72, 109, 0.06)',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          transition: theme.transitions.create(['width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Toolbar />
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Outlet />
        </Box>
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 30px rgba(9, 72, 109, 0.15)',
            mt: 1,
            minWidth: 220,
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #F0F0F6' }}>
          <Typography variant="body1" sx={{ fontWeight: 700, color: '#202647' }}>
            {admin?.name || 'Admin'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#7F839C', fontSize: '0.8rem' }}>
            {admin?.email || ''}
          </Typography>
          <Typography variant="caption" sx={{ color: '#11C1EC', fontWeight: 700, display: 'block', mt: 0.5 }}>
            {admin?.role === 'super_admin' ? 'Super Admin' : 'Admin'}
          </Typography>
        </Box>
        <MenuItem onClick={() => { navigate('/admin/settings'); handleProfileMenuClose(); }}
          sx={{ py: 1.5, '&:hover': { bgcolor: 'rgba(9, 72, 109, 0.05)' } }}>
          <AccountCircleIcon sx={{ mr: 1.5, color: '#7F839C', fontSize: 20 }} />
          <Typography variant="body2" sx={{ color: '#565972' }}>Profile</Typography>
        </MenuItem>
        <MenuItem onClick={() => { navigate('/admin/settings'); handleProfileMenuClose(); }}
          sx={{ py: 1.5, '&:hover': { bgcolor: 'rgba(9, 72, 109, 0.05)' } }}>
          <SettingsIcon sx={{ mr: 1.5, color: '#7F839C', fontSize: 20 }} />
          <Typography variant="body2" sx={{ color: '#565972' }}>Settings</Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}
          sx={{ py: 1.5, '&:hover': { bgcolor: 'rgba(255, 71, 87, 0.05)' } }}>
          <LogoutIcon sx={{ mr: 1.5, color: '#FF4757', fontSize: 20 }} />
          <Typography variant="body2" sx={{ color: '#FF4757' }}>Logout</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AdminLayout;
